import { Helmet } from 'react-helmet-async';
import { FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Person } from '@mui/icons-material';
import { Avatar, Box, styled, Typography } from '@mui/material';
import * as yup from 'yup';

import { useActivateUser } from '$components/pages/activate_user/activateUser.state';
import RotraTextField from '$reactHookForm/v2/RotraTextFieldV2';
import useRotraForm from '$reactHookForm/v2/useRotraForm';
import useMyRotraTranslations from '$utils/useMyRotraTranslations';
import { getInitials } from '$utils/UtilMethods';

export const PersonalDetailsStepValidation = yup.object().shape({
  firstname: yup
    .string()
    .trim()
    .required('First name is required')
    .min(1)
    .max(35),
  lastname: yup
    .string()
    .trim()
    .required('Last name is required')
    .min(1)
    .max(35),
});

type ActivationPersonalDetailsFormProps = {
  firstname: string;
  lastname: string;
};

const NameFieldsContainer = styled(Box)(() => ({
  display: 'grid',
  width: '100%',
  gridTemplateRows: '1 1',
  height: 120,
}));

export default function PersonalDetailsStep() {
  const { state, actions } = useActivateUser();
  const { tSpecific } = useMyRotraTranslations({
    pathPrefix: 'activate_user',
  });

  const methods = useRotraForm<ActivationPersonalDetailsFormProps>({
    formProps: {
      mode: 'all',
      delayError: 350,
      reValidateMode: 'onChange',
      resolver: yupResolver(PersonalDetailsStepValidation),
      defaultValues: {
        firstname: state.firstname,
        lastname: state.lastname,
      },
    },
    onChange: (value: ActivationPersonalDetailsFormProps) => {
      actions.updatePersonalDetails(value.firstname, value.lastname);
    },
  });

  const initialFirstname = methods.watch('firstname');
  const initialLastname = methods.watch('lastname');

  return (
    <>
      <Helmet>
        <title>{tSpecific('txt_who_are_you')}</title>
      </Helmet>
      <FormProvider {...methods}>
        <Typography variant="h5">
          <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
          {tSpecific('txt_who_are_you')}
        </Typography>
        <br />
        <Typography variant="body1" sx={{
          mb: 3
        }}>
          {tSpecific('txt_please_tell_us_about_yourself')}
        </Typography>
        <NameFieldsContainer>
          <RotraTextField
            required
            placeholder={tSpecific('ph_first_name')}
            size="small"
            margin="none"
            maxLength={35}
            fieldPath="firstname"
            type="string"
            label={tSpecific('lbl_first_name')}
            name="firstname"
          />
          <RotraTextField
            required
            placeholder={tSpecific('ph_last_name')}
            size="small"
            margin="none"
            maxLength={35}
            fieldPath="lastname"
            type="string"
            label={tSpecific('lbl_last_name')}
            name="lastname"
          />
        </NameFieldsContainer>

        {(initialFirstname || initialLastname) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
              justifyContent: 'center',
              mt: 3,
            }}
          >
            <Avatar>
              {getInitials(
                `${(initialFirstname || '').trim()} ${
                  initialLastname || ''.trim()
                }`
              )}
            </Avatar>{' '}
            <Typography>
              {initialFirstname} {initialLastname}
            </Typography>
          </Box>
        )}
      </FormProvider>
    </>
  );
}
